import { useState, useEffect } from "react";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Calendar, Loader2, AlertCircle, Eye } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import { obtenerReservasZonaComun, ReservaZonaComun } from "../../../../api/reservaZonaComunService";
import Swal from "sweetalert2";
import DetailReservaModal from "./DetailReservaModal";

export default function ReservaListPage() {
  const { session } = useAuth();
  const [reservas, setReservas] = useState<ReservaZonaComun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReservaCodigo, setSelectedReservaCodigo] = useState<string>("");

  useEffect(() => {
    const unidadId = session?.unidad?.id;
    
    if (unidadId) {
      loadReservas();
    }
  }, [session?.unidad?.id, session?.rol?.id]);

  const loadReservas = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const unidadCodigo = session?.unidad?.id || "";
      const rolId = session?.rol?.id || "";
      
      if (!unidadCodigo) {
        setReservas([]);
        return;
      }

      // Para los roles PT, AS y AD, no filtrar por apartamento (mostrar todas las reservas de la unidad)
      const rolesSinFiltroApartamento = ["PT", "AS", "AD"];
      const apartamentoCodigo = rolesSinFiltroApartamento.includes(rolId) 
        ? "" 
        : (session?.apartamento?.id || "");

      const payload = {
        "unidad residencial codigo": unidadCodigo,
        "zona comun codigo": "",
        "apartamento codigo": apartamentoCodigo,
        "fecha (dd/mm/aaaa)": "",
        "reserva zona comun codigo": "",
      };

      const response = await obtenerReservasZonaComun(payload);
      
      if (response.code === "1" && response.object_) {
        setReservas(response.object_);
      } else {
        setReservas([]);
        setError(response.message || "No se encontraron reservas");
      }
    } catch (error) {
      console.error("Error al cargar reservas:", error);
      setError("Error al cargar las reservas");
      setReservas([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las reservas",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReservaClick = (reservaCodigo: string) => {
    setSelectedReservaCodigo(reservaCodigo);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = (shouldReload?: boolean) => {
    setShowDetailModal(false);
    setSelectedReservaCodigo("");
    if (shouldReload) {
      loadReservas();
    }
  };

  const getStatusColor = (estatus: string) => {
    const estatusUpper = (estatus || "").toUpperCase();
    if (estatusUpper === "P" || estatusUpper.includes("PENDIENTE")) {
      return "bg-yellow-100 text-yellow-800 border border-yellow-300";
    } else if (estatusUpper === "S" || estatusUpper.includes("SEPARADA") || estatusUpper.includes("APROBADA")) {
      return "bg-green-100 text-green-800 border border-green-300";
    } else if (estatusUpper === "N" || estatusUpper.includes("NO APROBADA")) {
      return "bg-red-100 text-red-800 border border-red-300";
    }
    return "bg-gray-100 text-gray-800 border border-gray-300";
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Sin fecha";
    try {
      if (dateString.includes("/")) {
        const parts = dateString.split("/");
        if (parts.length === 3 && parts[0].length === 4) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100 text-gray-900">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-military-600">
            Reservas de Zonas Comunes
          </h2>
        </div>

        <Card className="shadow-lg border-l-4 border-military-500">
          <CardHeader>
            <CardTitle className="text-xl text-military-600 flex items-center gap-2">
              <Calendar size={24} />
              Lista de Reservas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-military-600" />
                <span className="ml-2 text-gray-600">Cargando reservas...</span>
              </div>
            ) : error && reservas.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <AlertCircle className="h-8 w-8 text-red-500" />
                <span className="ml-2 text-gray-600">{error}</span>
              </div>
            ) : reservas.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar size={48} className="mx-auto mb-4 text-gray-400" />
                <p>No hay reservas disponibles.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {reservas.map((reserva) => (
                  <Card
                    key={reserva.rzc_codigo}
                    className="bg-white rounded-xl border-2 border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer hover:border-military-400 transform hover:-translate-y-1 flex flex-col"
                    onClick={() => handleReservaClick(reserva.rzc_codigo)}
                  >
                    {/* Imagen de la zona común */}
                    {reserva.rzc_ruta_foto_principal ? (
                      <div className="relative w-full h-48 sm:h-56 md:h-48 lg:h-52 overflow-hidden bg-gray-200">
                        <img
                          src={reserva.rzc_ruta_foto_principal}
                          alt={reserva.rzc_zona_comun_nombre}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/placeholder.svg';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="relative w-full h-48 sm:h-56 md:h-48 lg:h-52 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <Calendar className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                    
                    <CardContent className="p-4 sm:p-5 flex-1 flex flex-col">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-base sm:text-lg font-bold text-gray-900 line-clamp-2 flex-1 pr-2">
                            {reserva.rzc_zona_comun_nombre}
                          </h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReservaClick(reserva.rzc_codigo);
                            }}
                            className="text-military-600 hover:text-military-700 flex-shrink-0"
                          >
                            <Eye size={18} />
                          </Button>
                        </div>
                        
                        <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar size={12} className="flex-shrink-0" />
                            <span className="truncate">{formatDate(reserva.rzc_fecha_separacion)}</span>
                          </div>
                          <div className="flex items-start gap-2 text-gray-600">
                            <span className="font-semibold flex-shrink-0">Día:</span>
                            <span className="truncate">{reserva.rzc_dia_reservado_nombre}</span>
                          </div>
                          <div className="flex items-start gap-2 text-gray-600">
                            <span className="font-semibold flex-shrink-0">Hora:</span>
                            <span className="truncate">{reserva.rzc_hora_inicio_reservada} - {reserva.rzc_hora_final_reservada}</span>
                          </div>
                          <div className="flex items-start gap-2 text-gray-600">
                            <span className="font-semibold flex-shrink-0">Apto:</span>
                            <span className="truncate">{reserva.rzc_apartamento_nombre}</span>
                          </div>
                          <div className="flex items-start gap-2 text-gray-600">
                            <span className="font-semibold flex-shrink-0">Por:</span>
                            <span className="truncate">{reserva.rzc_nombre_de_reservador}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-semibold flex-shrink-0">Valor:</span>
                            <span className="font-bold text-green-600 truncate">
                              ${reserva.rzc_valor.toLocaleString('es-CO')}
                            </span>
                          </div>
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <span className={`inline-block px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-bold ${getStatusColor(reserva.rzc_estatus)}`}>
                            {reserva.rzc_estatus_nombre}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <DetailReservaModal
          isOpen={showDetailModal}
          reservaCodigo={selectedReservaCodigo}
          onClose={handleCloseDetailModal}
        />
      </main>
    </div>
  );
}
